import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core'
import {
  Close,
  History,
  Settings,
  FormatListNumbered,
} from '@material-ui/icons'
import MuiMarkdown from 'mui-markdown'
import React, { forwardRef, useEffect, useState } from 'react'
import {
  AppBar,
  AppBarProps,
  CoreLayoutProps,
  Labeled,
  MenuItemLink,
  TextField,
  UserMenu,
  UserMenuProps,
  sanitizeFetchType,
  useQuery,
  useTranslate,
} from 'react-admin'
import { useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { parseVersion } from '../../core/common/visual.utils'
import { ThemeState } from '../../core/common/theme.reducer'
import { Layout } from '../../lib/@react-admin/ra-enterprise/esm/src'
import ApiErrorModal from '../common/ApiErrorModal'
import { extendedDarkTheme, extendedLightTheme } from './app.theme'
import { AppMenu } from './AppMenu'
import guiVersionJson from '../../build.json'
import { ResourceName } from '../../core/ResourceName'
import DraggableComponent from '../common/DraggableComponent'

const useStyles = makeStyles((theme) => ({
  title: {
    flex: 1,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  bulkStateActions: {
    height: '16px',
    background: 'rgba(0,0,0,0.2)',
    minWidth: '300px',
    userSelect: 'none',
  },
  bulkProgressTitle: {
    paddingLeft: '10px',
    width: '100%',
  },
  bulkProgressCloseButton: {
    margin: 0,
  },
  menuItem: {
    color: theme.palette.text.secondary,
  },
  menuItemIcon: {
    minWidth: theme.spacing(5),
  },
  dialogContent: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  testBanner: {
    fontWeight: 700,
    letterSpacing: 2,
    borderTop: '1px solid #424242',
    fontSize: '0.75rem',
    minHeight: 20,
  },
}))

const AppVersionDialog = ({ ...props }) => {
  const { open, handleClose } = props
  const classes = useStyles()
  const translate = useTranslate()

  const { InformationalVersion: guiVersion } = guiVersionJson
  const { data: apiVersionResponse } = useQuery(
    {
      type: sanitizeFetchType('getAPIVersion'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: open,
    },
  )

  const versions = { apiVersion: apiVersionResponse?.version, guiVersion }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('versions.name')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent className={classes.dialogContent}>
        <Labeled label="versions.fields.apiVersion" source="apiVersion">
          <TextField record={versions as any} source="apiVersion" />
        </Labeled>
        <Labeled label="versions.fields.guiVersion" source="guiVersion">
          <TextField record={versions as any} source="guiVersion" />
        </Labeled>
      </DialogContent>
    </Dialog>
  )
}

const ChangelogDialog = ({ ...props }) => {
  const { open, handleClose } = props
  const classes = useStyles()
  const translate = useTranslate()
  const [markdownText, setMarkdownText] = useState<string>('')

  const { data: changelogResponse, loaded: changelogLoaded } = useQuery(
    {
      type: sanitizeFetchType('getChangelog'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: open,
    },
  )

  useEffect(() => {
    if (changelogLoaded && changelogResponse?.blob) {
      changelogResponse.blob.body.text().then((text: string) => {
        setMarkdownText(text)
      })
    }
  }, [changelogResponse, changelogLoaded])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="md"
      fullWidth
    >
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('versions.changelog')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent dividers>
        <MuiMarkdown>{markdownText}</MuiMarkdown>
      </DialogContent>
    </Dialog>
  )
}

const ChangelogButton = ({ ...props }) => {
  const { handleChangelogDialog, onClick, ...rest } = props
  const translate = useTranslate()
  const classes = useStyles()

  const handleOnClick = () => {
    handleChangelogDialog()
    onClick()
  }

  return (
    <MenuItem {...rest} className={classes.menuItem} onClick={handleOnClick}>
      <ListItemIcon className={classes.menuItemIcon}>
        <FormatListNumbered />
      </ListItemIcon>
      {translate('versions.changelog')}
    </MenuItem>
  )
}

const AppVersionButton = ({ ...props }) => {
  const { handleOpenVersionDialog, onClick, ...rest } = props
  const translate = useTranslate()
  const classes = useStyles()

  const handleOnClick = () => {
    handleOpenVersionDialog()
    onClick()
  }

  return (
    <MenuItem {...rest} className={classes.menuItem} onClick={handleOnClick}>
      <ListItemIcon className={classes.menuItemIcon}>
        <History />
      </ListItemIcon>
      {translate('versions.name')}
    </MenuItem>
  )
}

interface TicketAPIUserMenuProps extends UserMenuProps {
  readonly handleOpenVersionDialog: () => void
  readonly handleChangelogDialog: () => void
}

const AppUserMenu = (props: TicketAPIUserMenuProps) => {
  const { handleOpenVersionDialog, handleChangelogDialog, ...rest } = props
  const translate = useTranslate()

  const AppVersionButtonForwardRef = forwardRef((forwardRefProps, ref) => (
    <AppVersionButton
      innerRef={ref}
      {...forwardRefProps}
      handleOpenVersionDialog={handleOpenVersionDialog}
    />
  ))

  const ChangelogButtonForwardRef = forwardRef((forwardRefProps, ref) => (
    <ChangelogButton
      innerRef={ref}
      {...forwardRefProps}
      handleChangelogDialog={handleChangelogDialog}
    />
  ))

  return (
    <UserMenu {...rest}>
      <MenuItemLink
        to="/configuration"
        primaryText={translate('settings.name')}
        leftIcon={<Settings />}
      />
      <AppVersionButtonForwardRef />
      <ChangelogButtonForwardRef />
    </UserMenu>
  )
}

const TestApplicationBanner = () => {
  const translate = useTranslate()
  const classes = useStyles()

  const { data } = useQuery(
    {
      type: sanitizeFetchType('getAPIVersion'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: true,
    },
  )

  const { versionNumber, branch } = data?.version
    ? parseVersion(data.version)
    : { versionNumber: undefined, branch: undefined }

  if (!versionNumber || branch === 'release') return null

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      width="100vw"
      zIndex={2000}
      bgcolor="error.main"
      color="common.white"
      textAlign="center"
      py={0.5}
      className={classes.testBanner}
    >
      {translate('const.testVersion')}
    </Box>
  )
}

const AppTopBar = (props: AppBarProps) => {
  const classes = useStyles()
  const modalEl = document.getElementById('api-error-modal')

  const [versionDialogOpen, setVersionDialogOpen] = useState<boolean>(false)
  const [changelogOpen, setChangelogOpen] = useState<boolean>(false)

  const handleOpenVersionDialog = () => {
    setVersionDialogOpen(true)
  }

  const handleCloseVersionDialog = () => {
    setVersionDialogOpen(false)
  }

  const handleOpenChangelogDialog = () => {
    setChangelogOpen(true)
  }

  const handleCloseChangelogDialog = () => {
    setChangelogOpen(false)
  }

  return (
    <>
      {modalEl && createPortal(<ApiErrorModal />, modalEl)}
      <AppVersionDialog
        open={versionDialogOpen}
        handleClose={handleCloseVersionDialog}
      />
      <ChangelogDialog
        open={changelogOpen}
        handleClose={handleCloseChangelogDialog}
      />
      <AppBar
        {...props}
        userMenu={
          <AppUserMenu
            handleOpenVersionDialog={handleOpenVersionDialog}
            handleChangelogDialog={handleOpenChangelogDialog}
          />
        }
        position="absolute"
      >
        <Typography
          variant="h6"
          className={classes.title}
          id="react-admin-title"
        />
      </AppBar>
      <TestApplicationBanner />
    </>
  )
}

export const AppLayout = (props: CoreLayoutProps) => {
  const theme = useSelector((state: ThemeState) =>
    state.theme.darkMode === 'dark' ? extendedDarkTheme : extendedLightTheme,
  )
  const location = useLocation()
  const { children } = props

  const isAuthRoute =
    location.pathname === '/send-reset-password-link' ||
    location.pathname === '/password-expired' ||
    location.pathname === '/reset-password'

  if (isAuthRoute) {
    return <div>{children}</div>
  }

  return <Layout {...props} menu={AppMenu} appBar={AppTopBar} theme={theme} />
}
